/**
 * Phase 200 — Smart Parking live auto-refresh (~10 s polling, no WebSockets).
 *
 * Every parking write bumps ONE tenant-wide counter (`GET /t/{code}/parking/{scope}/live-version`). One poller per
 * (code, scope) reads it every POLL_MS while the tab is visible and again when the tab regains focus; when the number
 * changes every subscribed component refetches. Idle pages cost one tiny request every 10 s.
 *
 *   const live = useLive(code, "admin");                         // number that changes when parking data changed
 *   useEffect(() => { load(); }, [code, live]);
 *   useLive(code, "citizen", () => reload({ silent: true }));    // or pass a callback
 *   pokeLive(code, "admin");                                      // right after THIS tab wrote something
 */
import { useEffect, useRef, useState } from "react";
import { api } from "./api";

export const POLL_MS = 10000;
const MIN_GAP_MS = 1200;
const pollers = new Map(); // key -> state

function key(code, scope) {
  return `${code}:${scope}`;
}

function get(code, scope) {
  const k = key(code, scope);
  if (!pollers.has(k)) pollers.set(k, { code, scope, version: null, timer: null, inflight: null, lastCheck: 0, updatedAt: null, subs: new Set(), onWake: null });
  return pollers.get(k);
}

async function check(st, reason = "poll") {
  if (st.subs.size === 0) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden" && reason !== "poke") return;
  if (reason !== "poke" && Date.now() - st.lastCheck < MIN_GAP_MS) return;
  if (st.inflight) return st.inflight;
  st.inflight = api.get(`/t/${st.code}/parking/${st.scope}/live-version`)
    .then((r) => {
      const v = r.data?.version;
      if (typeof v !== "number") return;
      const changed = st.version !== null && v !== st.version;
      st.version = v;
      st.updatedAt = r.data?.updated_at || null;
      st.lastCheck = Date.now();
      if (changed) st.subs.forEach((fn) => { try { fn({ version: v, reason, updatedAt: st.updatedAt }); } catch { /* ignore */ } });
    })
    .catch(() => { st.lastCheck = Date.now(); })
    .finally(() => { st.inflight = null; });
  return st.inflight;
}

function start(st) {
  if (st.timer) return;
  st.version = null;
  st.timer = setInterval(() => check(st, "poll"), POLL_MS);
  st.onWake = () => { if (typeof document === "undefined" || document.visibilityState !== "hidden") check(st, "focus"); };
  window.addEventListener("focus", st.onWake);
  document.addEventListener("visibilitychange", st.onWake);
}

function stop(st) {
  if (!st.timer) return;
  clearInterval(st.timer);
  st.timer = null;
  if (st.onWake) {
    window.removeEventListener("focus", st.onWake);
    document.removeEventListener("visibilitychange", st.onWake);
  }
}

/** Re-check at once (same-tab writes). */
export function pokeLive(code, scope) {
  const st = get(code, scope);
  setTimeout(() => { check(st, "poke"); }, 150);
}

export function useLive(code, scope, onChange, { enabled = true } = {}) {
  const [version, setVersion] = useState(0);
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    if (!enabled || !code || !scope) return undefined;
    const st = get(code, scope);
    const fn = (payload) => { setVersion(payload.version); if (cb.current) cb.current(payload); };
    st.subs.add(fn);
    if (st.subs.size === 1) start(st);
    check(st, "mount");
    return () => { st.subs.delete(fn); if (st.subs.size === 0) stop(st); };
  }, [code, scope, enabled]);
  return version;
}
